import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Wrench, 
  ShoppingBag, 
  BookOpen, 
  Award, 
  User, 
  Bell, 
  Settings, 
  LogOut,
  Menu,
  X,
  Video,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { ThemeToggle } from '../components/ui/themeToggle';
import { Button } from '../components/ui/Button';
import { Avatar } from '../components/ui/Avatar';
import { useToast } from '../components/ui/ToastProvider';
import { MobileBottomNav } from '../components/MobileBottomNav';

interface NavItem {
  label: string;
  icon: React.ElementType;
  path: string;
  badge?: number;
}

const navItems: NavItem[] = [
  { label: 'Dashboard', icon: LayoutDashboard, path: '/dashboard' },
  { label: 'My Repairs', icon: Wrench, path: '/dashboard/repairs' },
  { label: 'My Orders', icon: ShoppingBag, path: '/orders' },
  { label: 'My Courses', icon: BookOpen, path: '/dashboard/courses' },
  { label: 'My Live Classes', icon: Video, path: '/dashboard/live-classes' },
  { label: 'My Certificates', icon: Award, path: '/dashboard/certificates' },
  { label: 'Profile', icon: User, path: '/dashboard/profile' },
  { label: 'Notifications', icon: Bell, path: '/dashboard/notifications', badge: 3 },
  { label: 'Settings', icon: Settings, path: '/dashboard/settings' },
];

export const DashboardLayout: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  const handleLogout = async () => {
    try {
      await logout();
      showToast('Logged out successfully', 'success');
      navigate('/');
    } catch {
      showToast('Failed to logout', 'error');
    }
  };

  const toggleSidebar = () => {
    if (window.innerWidth < 768) {
      setIsMobileSidebarOpen(!isMobileSidebarOpen);
    } else {
      setIsSidebarOpen(!isSidebarOpen);
    }
  };

  // Close mobile sidebar on route change
  const handleNavClick = () => {
    if (window.innerWidth < 768) {
      setIsMobileSidebarOpen(false);
    }
  };

  const sidebarContent = (
    <div className="flex h-full flex-col">
      {/* Sidebar Header */}
      <div className="flex h-16 items-center justify-between border-b border-[rgb(var(--border))] px-4">
        <div className="flex items-center space-x-2">
          <img src="/logo.png" alt="EdyTech" className="h-8 w-auto" />
          <span className="text-lg font-bold text-primary">EdyTech</span>
        </div>
        <button
          onClick={toggleSidebar}
          className="rounded-lg p-1 hover:bg-[rgb(var(--border))] transition-colors"
        >
          {isSidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {/* User Profile */}
      <div className="border-b border-[rgb(var(--border))] p-4">
        <div className="flex items-center space-x-3">
          <Avatar name={user?.name || 'User'} size="md" />
          <div className="flex-1 min-w-0">
            <p className="truncate text-sm font-medium text-[rgb(var(--foreground))]">
              {user?.name}
            </p>
            <p className="truncate text-xs text-[rgb(var(--light-secondary))]">
              {user?.email}
            </p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-1 overflow-y-auto p-3">
        {navItems.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={handleNavClick}
              className={({ isActive }) =>
                `flex items-center justify-between rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-primary/10 text-primary'
                    : 'text-[rgb(var(--light-secondary))] hover:bg-[rgb(var(--border))] hover:text-[rgb(var(--foreground))]'
                }`
              }
            >
              <div className="flex items-center space-x-3">
                <Icon className="h-5 w-5" />
                <span>{item.label}</span>
              </div>
              {item.badge && (
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary text-xs text-white">
                  {item.badge}
                </span>
              )}
            </NavLink>
          );
        })}
      </nav>

      {/* Sidebar Footer */}
      <div className="border-t border-[rgb(var(--border))] p-4 space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm text-[rgb(var(--light-secondary))]">Theme</span>
          <ThemeToggle />
        </div>
        <button
          onClick={handleLogout}
          className="flex w-full items-center space-x-2 rounded-lg px-3 py-2 text-sm font-medium text-red-500 hover:bg-red-500/10 transition-colors"
        >
          <LogOut className="h-5 w-5" />
          <span>Logout</span>
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-[rgb(var(--background))]">
      {/* Desktop Sidebar */}
      <aside
        className={`hidden md:block fixed inset-y-0 left-0 z-40 w-64 border-r border-[rgb(var(--border))] bg-[rgb(var(--card))] transition-transform duration-300 ${
          isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {sidebarContent}
      </aside>

      {/* Mobile Sidebar Overlay */}
      {isMobileSidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 md:hidden"
          onClick={() => setIsMobileSidebarOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-64 bg-[rgb(var(--card))] transition-transform duration-300 md:hidden ${
          isMobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {sidebarContent}
      </aside>

      {/* Main Content */}
      <div
        className={`flex-1 transition-all duration-300 ${
          isSidebarOpen ? 'md:ml-64' : 'md:ml-0'
        }`}
      >
        {/* Top Bar */}
        <header className="sticky top-0 z-30 border-b border-[rgb(var(--border))] bg-[rgb(var(--background))] backdrop-blur-sm">
          <div className="flex h-16 items-center justify-between px-4 md:px-8">
            <div className="flex items-center space-x-4">
              <button
                onClick={toggleSidebar}
                className="rounded-lg p-2 hover:bg-[rgb(var(--border))] transition-colors"
              >
                <Menu className="h-5 w-5" />
              </button>
              <h1 className="text-xl font-semibold text-[rgb(var(--foreground))]">
                Dashboard
              </h1>
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="ghost"
                size="sm"
                className="relative rounded-full p-2"
                onClick={() => navigate('/dashboard/notifications')}
              >
                <Bell className="h-5 w-5" />
                {navItems.find(n => n.path === '/dashboard/notifications')?.badge && (
                  <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-xs text-white">
                    {navItems.find(n => n.path === '/dashboard/notifications')?.badge}
                  </span>
                )}
              </Button>
              <button
                onClick={() => navigate('/dashboard/profile')}
                className="rounded-full p-1 hover:ring-2 hover:ring-primary transition-all"
              >
                <Avatar name={user?.name || 'User'} size="sm" />
              </button>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 pb-24 md:p-8">
          <Outlet />
        </main>
        <MobileBottomNav />
      </div>
    </div>
  );
};